import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Chip,
  Button,
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Description as MarkdownIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { Modal } from '../../../common/components/Modal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import YouTubeVideo from './YouTubeVideo';

interface HelpContent {
  id: string;
  moduleId: string;
  title: string;
  content: string;
  contentType: 'markdown' | 'video';
  videoUrl?: string;
  createdAt: string;
  updatedAt: string;
  dataTestId?: string;
}

interface HelpContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: HelpContent | null;
}

/** Shared `a` renderer for ReactMarkdown: download chip for /statics/ URLs, regular link otherwise */
const ARenderer = ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement> & { children?: React.ReactNode }) => {
  const isAttachment = href && (href.includes('/statics/') || href.includes('blob.core.windows.net'));
  if (isAttachment) {
    return (
      <Button
        component="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        download
        size="small"
        variant="outlined"
        startIcon={<DownloadIcon fontSize="small" />}
        sx={{
          my: 0.5,
          textTransform: 'none',
          fontSize: '0.875rem',
          borderColor: '#00B6BA',
          color: '#00B6BA',
          '&:hover': { borderColor: '#008a8d', color: '#008a8d', backgroundColor: 'rgba(0,182,186,0.04)' },
        }}
        {...(props as any)}
      >
        {children}
      </Button>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  );
};

const HelpContentModal: React.FC<HelpContentModalProps> = ({
  isOpen,
  onClose,
  content,
}) => {
  const { t } = useTranslation();

  if (!content) return null;

  const renderContent = () => {
    if (content.contentType === 'video') {
      // Check if it's a YouTube URL
      const youtubeMatch = (content.videoUrl || content.content).match(
        /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/
      );
      
      if (youtubeMatch) {
        const videoId = youtubeMatch[1];
        return (
          <Box sx={{ width: '100%', maxWidth: '100%' }}>
            <YouTubeVideo 
              videoId={videoId}
              title={content.title}
            />
          </Box>
        );
      }

      // Fallback to regular video player
      return (
        <Box sx={{ width: '100%', maxWidth: '100%' }}>
          <video
            controls
            style={{ width: '100%', maxHeight: '400px' }}
            src={content.videoUrl || content.content}
          >
            {t('help.videoNotSupported', 'Your browser does not support the video tag.')}
          </video>
        </Box>
      );
    }

    return (
      <Box 
        data-testid="help-content-modal-render-content"
        sx={{ 
          minHeight: '200px',
        '& h1, & h2, & h3, & h4, & h5, & h6': {
          marginTop: 2,
          marginBottom: 1,
          fontWeight: 600,
          color: '#063142',
        },
        '& h1': {
          fontSize: '1.5rem',
          borderBottom: '2px solid #E1E8ED',
          paddingBottom: '0.5rem',
        },
        '& h2': {
          fontSize: '1.25rem',
          borderBottom: '1px solid #E1E8ED',
          paddingBottom: '0.25rem',
        },
        '& h3': {
          fontSize: '1.1rem',
        },
        '& p': {
          marginBottom: 1,
          lineHeight: 1.6,
        },
        '& ul, & ol': {
          marginBottom: 1,
          paddingLeft: 2,
        },
        '& li': {
          marginBottom: '0.25rem',
        },
        '& code': {
          backgroundColor: '#f5f5f5',
          padding: '0.125rem 0.25rem',
          borderRadius: '0.25rem',
          fontSize: '0.875rem',
          fontFamily: 'monospace',
        },
        '& pre': {
          backgroundColor: '#f8f9fa',
          padding: '1rem',
          borderRadius: '0.5rem',
          overflowX: 'auto',
          border: '1px solid #e9ecef',
          marginBottom: 1,
        },
        '& pre code': {
          backgroundColor: 'transparent',
          padding: 0,
        },
        '& blockquote': {
          borderLeft: '4px solid #00B6BA',
          paddingLeft: '1rem',
          marginLeft: 0,
          fontStyle: 'italic',
          color: '#666',
        },
        '& table': {
          borderCollapse: 'collapse',
          width: '100%',
          marginBottom: 1,
        },
        '& th, & td': {
          border: '1px solid #ddd',
          padding: '0.5rem',
          textAlign: 'left',
        },
        '& th': {
          backgroundColor: '#f8f9fa',
          fontWeight: 600,
        },
        '& a': {
          color: '#00B6BA',
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        '& strong': {
          fontWeight: 600,
        },
        '& em': {
          fontStyle: 'italic',
        },
        '& iframe': {
          width: '100%',
          height: '315px',
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        },
        '& img': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
        '& video': {
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      }}>
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]} 
          rehypePlugins={[rehypeRaw]}
          components={{
            a: ARenderer,
            img: ({ src, alt, ...props }) => {
              // Log image URL
              console.log('🖼️ Image URL:', src);
              
              return (
                <img 
                  src={src} 
                  alt={alt || 'Image'} 
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    display: 'block',
                    margin: '10px 0',
                  }}
                  onLoad={() => {
                    console.log('✅ Loaded:', alt || 'Image');
                  }}
                  onError={() => {
                    console.log('❌ Failed:', src);
                  }}
                  {...props}
                />
              );
            },
          }}
        >
          {content.content}
        </ReactMarkdown>
      </Box>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={content.title}
      maxWidth="md"
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          margin: 'auto',
        }
      }}
      scroll="body"
      disableSave
      applyText=""
      cancelText={t('common.close', 'Close')}
      data-testid="help-content-modal"
    >
      <Box sx={{ mb: 2, flex: 1, overflowY: 'auto', overflowX: 'hidden' }} data-testid="help-content-modal-content">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }} data-testid="help-content-modal-header">
          <Chip
            icon={content.contentType === 'video' ? <PlayIcon /> : <MarkdownIcon />}
            label={content.contentType === 'video' 
              ? t('help.videoContent', 'Video Content') 
              : t('help.markdownContent', 'Markdown Content')
            }
            size="small"
            color="primary"
            variant="outlined"
          />
          <Typography variant="caption" color="text.secondary">
            {t('help.lastUpdated', 'Last updated:')} {new Date(content.updatedAt).toLocaleDateString()}
          </Typography>
        </Box>
        {renderContent()}
      </Box>
    </Modal>
  );
};

export default HelpContentModal; 